<template>
  <div
    :class="{
      [$style.vgp_chips]: true,
      [$style.vgp_chips_color]: preset === 'color',
      [$style.vgp_chips_label]: preset === 'label',
      [$style.ellipsis_true]: txtEllipsis,
      [$style.width100]: fillWidth
    }"
    :style="presetStyle"
    @click="$emit('onClick', $event)"
  >
    <vgp-label
      v-if="preset === 'label'"
      :value="text"
      :bg-color="bgColor"
      :text-color="txtColor !== 'inherit' ? txtColor : undefined"
      :size="labelSize"
    />

    <template v-else>
      <div
        v-if="useImgUrl"
        :class="$style.img_wrapper"
      >
        <vgp-avatar
          v-if="useIsAvatar"
          :src="imgUrl"
          size="small"
        />
        <div
          v-else
          :class="$style.vgp_chips_image"
          :style="{
            'background-image': 'url('+ imgUrl + ')',
            width: imgSize + 'px',
            height: imgSize + 'px',
          }"
        />
      </div>

      <div
        v-if="useStatusColor"
        :class="$style.circle_status"
        :style="{ 'background-color': statusColor }"
      />

      <svg-sprite
        v-if="useIcon"
        :name="icon.name"
        :type="icon.type"
        :size="icon.size"
        :color="selected ? 'inherit' : icon.color"
        :style="{ 'margin-right': '4px' }"
      />

      <div
        v-if="useText || (description && needDescription)"
        :class="[$style.vgp_chips_name_block, $style.ellipsis]"
      >
        <span :class="[$style.vgp_chips_name, $style.ellipsis]">{{ text }}</span>
        <span
          v-if="description && needDescription"
          :class="[$style.vgp_chips_description, selected && $style.vgp_chips_description_select]"
        >
          {{ description }}
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import svgSprite from '$$uiKitComp/VueIcon/svgSprite.vue';
import vgpLabel from '$$vueCmp/label/vgpLabel.vue';
import vgpAvatar from '$$uiKitComp/avatar/vgpAvatar.vue';

const PRESETS_OPT = {
  text: ['text', 'txtColor'],
  user: ['text', 'imgUrl', 'isAvatar'],
  withImg: ['text', 'imgUrl'],
  withIcon: ['text', 'icon', 'imgUrl'],
  avatar: ['isAvatar', 'imgUrl', 'bgColor'],
  status: ['text', 'statusColor'],
  color: ['bgColor'],
};

const PRESET_STYLES = {
  text: { paddings: [0, 8, 0, 8], borderRadius: 0 },
  user: { paddings: [0, 8, 0, 8], borderRadius: 0 },
  withImg: { paddings: [0, 8, 0, 8], borderRadius: 0 },
  withIcon: { paddings: [0, 8, 0, 8], borderRadius: 0 },
  avatar: { paddings: [0, 0, 0, 0], borderRadius: 0 },
  status: { paddings: [0, 0, 0, 0], borderRadius: 0 },
  color: { paddings: [0, 8, 0, 8], borderRadius: 4 },
};

const LABEL_SIZE_MAP = {
  xs: 'extraExtraSmall',
  sm: 'small',
  lg: 'large',
};

const AVATAR_SIZE = 24;

function getPaddings(top, right, bottom, left) {
  return {
    'padding-top': `${top}px`,
    'padding-right': `${right}px`,
    'padding-bottom': `${bottom}px`,
    'padding-left': `${left}px`,
  };
}

export default {
  name: 'VgpChip',
  components: { svgSprite, vgpLabel, vgpAvatar },

  props: {
    preset: {
      type: String,
      default: 'custom',
      validator: v => ['text', 'user', 'label', 'withImg', 'withIcon', 'custom', 'avatar', 'status', 'color'].includes(v),
    },
    text: { type: String, default: '' },
    description: { type: String, default: '' },
    imgUrl: { type: String, default: '' },
    imgSize: { type: Number, default: 20 },
    isAvatar: { type: Boolean, default: false },
    icon: { type: Object, default: null },
    statusColor: { type: String, default: '' },
    bgColor: { type: String, default: 'transparent' },
    txtColor: { type: String, default: 'inherit' },
    round: { type: Number, default: 60 },
    size: { type: String, default: 'sm' },
    paddingTop: { type: Number, default: 0 },
    paddingRight: { type: Number, default: 0 },
    paddingBottom: { type: Number, default: 0 },
    paddingLeft: { type: Number, default: 0 },
    grayTheme: { type: Boolean, default: false },
    txtEllipsis: { type: Boolean, default: false },
    fillWidth: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    needDescription: { type: Boolean, default: false },
  },

  emits: ['onClick'],

  computed: {
    useImgUrl() { return this._isUse('imgUrl'); },
    useIsAvatar() { return this._isUse('isAvatar'); },
    useStatusColor() { return this._isUse('statusColor'); },
    useIcon() { return this._isUse('icon'); },
    useText() { return this._isUse('text'); },

    labelSize() {
      return LABEL_SIZE_MAP[this.size] || 'small';
    },

    avatarMinHeight() {
      const textLineHeight = this.size === 'xs' ? 20 : 22;

      if (this.preset === 'avatar' || this.useIsAvatar) {
        return `${Math.max(textLineHeight, AVATAR_SIZE)}px`;
      }

      return `${textLineHeight}px`;
    },

    paddings() {
      const isCustom = this.preset === 'custom';

      if (!this.grayTheme && !isCustom) return getPaddings(0, 0, 0, 0);

      const p = isCustom
        ? [this.paddingTop, this.paddingRight, this.paddingBottom, this.paddingLeft]
        : PRESET_STYLES[this.preset].paddings;

      return getPaddings(...p);
    },

    bgColorStyle() {
      if (this.preset === 'avatar') {
        return 'transparent';
      }

      if (this.useIsAvatar && !this.imgUrl) {
        return 'transparent';
      }

      if (this.grayTheme) return '#f2f2f2';
      if (this.preset === 'custom' || this.preset === 'color') return this.bgColor;

      return 'transparent';
    },

    txtColorStyle() {
      if (this.preset === 'custom' || this._isUse('txtColor')) return this.txtColor;

      return 'inherit';
    },

    borderRadius() {
      if (this.grayTheme || this.preset === 'avatar') return '100px';
      if (this.preset === 'custom') return `${this.round}px`;

      return `${PRESET_STYLES[this.preset].borderRadius}px`;
    },

    presetStyle() {
      if (this.preset === 'label') return {};

      return {
        ...this.paddings,
        'border-radius': this.borderRadius,
        border: 'none',
        outline: 'none',
        'box-shadow': 'none',
        'font-size': this.size === 'xs' ? '12px' : this.size === 'lg' ? '16px' : '14px',
        'background-color': this.bgColorStyle,
        color: this.txtColorStyle,
        'min-height': this.avatarMinHeight,
        ...(this.size === 'xs' && this.preset !== 'avatar' && !this.useIsAvatar
          ? { height: '20px', maxHeight: '20px' }
          : {}),
      };
    },
  },

  methods: {
    _isUse(opt) {
      const val = this[opt];
      const exists = typeof val === 'boolean' ? val : !!val;

      if (this.preset === 'custom') return exists;

      const presetOpts = PRESETS_OPT[this.preset];

      if (!presetOpts) return exists;

      if (['avatar', 'user'].includes(this.preset) && presetOpts.includes(opt) && !exists) {
        return true;
      }

      return presetOpts.includes(opt) && exists;
    },
  },
};
</script>

<style module lang="less" src="./chip.less"></style>
